/**
 * roles.js — who may do what.
 *
 * WHAT THIS IS AND IS NOT
 * The AUTHORITATIVE check is in `firestore.rules` and in the Cloud Functions:
 * the database refuses a write a role may not make, whatever the browser
 * thinks. This module is the CLIENT'S MIRROR of that model, and it exists for
 * one reason — a button that is going to fail should not be offered.
 *
 * Hiding a control is not security. It is courtesy: a viewer who clicks "Cancel
 * invoice" and gets a permission error has been shown a lie about what they can
 * do, and has learned to distrust the interface.
 *
 * The roles are the four the server enforces. Adding one here without adding it
 * there would produce exactly the false confidence this comment warns about, so
 * the list is kept deliberately short and the server's own list is the source.
 */

/* `voucher-types` imports nothing, so there is no cycle. */
import { tryVoucherConfig, voucherTypeFromRoute } from './voucher-types';

export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  ACCOUNTANT: 'accountant',
  /**
   * Counter staff and a buyer.
   *
   * These two are the reason `CREATE_VOUCHER` had to be split. It was one
   * capability covering all ten document types, so there was no way to say
   * "may raise an invoice, may not raise a purchase bill" — which is the whole
   * distinction these roles exist to make. A shop that gives its counter staff
   * the ability to record purchases has given them the ability to invent a
   * supplier and a payable.
   */
  SALES_USER: 'sales_user',
  PURCHASE_USER: 'purchase_user',
  VIEWER: 'viewer',
};

export const ROLE_ORDER = [
  ROLES.OWNER,
  ROLES.ADMIN,
  ROLES.ACCOUNTANT,
  ROLES.SALES_USER,
  ROLES.PURCHASE_USER,
  ROLES.VIEWER,
];

export const ROLE_CONFIG = {
  [ROLES.OWNER]: {
    value: ROLES.OWNER,
    label: 'Owner',
    description:
      'Everything, including the business profile, other people’s roles, and removing members. There must always be at least one.',
    rank: 0,
  },
  [ROLES.ADMIN]: {
    value: ROLES.ADMIN,
    label: 'Manager',
    description:
      'Everything day to day: billing, purchases, stock, parties, payments. Can invite people but cannot make somebody an owner.',
    rank: 1,
  },
  [ROLES.ACCOUNTANT]: {
    value: ROLES.ACCOUNTANT,
    label: 'Accountant',
    description:
      'Books and filings: vouchers, payments, reports, GST returns. Cannot change the business profile or the team.',
    rank: 2,
  },
  [ROLES.SALES_USER]: {
    value: ROLES.SALES_USER,
    label: 'Sales Staff',
    description:
      'Bills customers and takes payment. Can raise quotations, orders, challans, invoices and credit notes, and nothing on the buying side.',
    rank: 3,
  },
  [ROLES.PURCHASE_USER]: {
    value: ROLES.PURCHASE_USER,
    label: 'Purchase Staff',
    description:
      'Buys and receives. Can raise purchase orders, bills and debit notes, record what goes out to suppliers, and move stock. Cannot bill a customer.',
    rank: 4,
  },
  [ROLES.VIEWER]: {
    value: ROLES.VIEWER,
    label: 'Viewer',
    description: 'Can look at everything and change nothing. For an auditor, or somebody being trained.',
    rank: 5,
  },
};

/** Roles that may be handed out. Owner is deliberately absent — see below. */
export const INVITABLE_ROLES = [
  ROLES.ADMIN,
  ROLES.ACCOUNTANT,
  ROLES.SALES_USER,
  ROLES.PURCHASE_USER,
  ROLES.VIEWER,
];

export const ROLE_OPTIONS = ROLE_ORDER.map((role) => ({
  value: role,
  label: ROLE_CONFIG[role].label,
}));

export const INVITABLE_ROLE_OPTIONS = INVITABLE_ROLES.map((role) => ({
  value: role,
  label: ROLE_CONFIG[role].label,
}));

export function roleConfig(role) {
  return ROLE_CONFIG[role] || ROLE_CONFIG[ROLES.VIEWER];
}

export function roleLabel(role) {
  return roleConfig(role).label;
}

/* ------------------------------------------------------------------ *
 * Capabilities
 * ------------------------------------------------------------------ */

/**
 * What each role can do, named by ACTION rather than by screen.
 *
 * Naming them by action is what keeps this honest as the application grows: a
 * new screen has to say which existing capability it needs, rather than
 * quietly inventing a rule of its own. A screen that needs a capability nobody
 * has yet is a conversation, not a silent default.
 */
export const CAPABILITIES = {
  /**
   * Documents.
   *
   * `CREATE_VOUCHER` is kept as the "may raise any document" capability that
   * an owner, a manager and an accountant hold. The two directional ones below
   * are what let a role hold half of it: `voucherCapabilityFor` maps a voucher
   * type onto the one it needs, so a screen asks about the document in front
   * of it rather than about a role.
   */
  CREATE_VOUCHER: 'voucher:create',
  CREATE_SALES_VOUCHER: 'voucher:create:sales',
  CREATE_PURCHASE_VOUCHER: 'voucher:create:purchase',
  CANCEL_VOUCHER: 'voucher:cancel',
  EDIT_VOUCHER: 'voucher:edit',
  RECORD_PAYMENT: 'payment:record',

  /* Masters */
  MANAGE_ITEMS: 'items:manage',
  MANAGE_PARTIES: 'parties:manage',
  MANAGE_PRICING: 'pricing:manage',

  /* Stock */
  ADJUST_STOCK: 'stock:adjust',
  TRANSFER_STOCK: 'stock:transfer',
  STOCK_TAKE: 'stock:take',

  /* Money */
  MANAGE_ACCOUNTS: 'accounts:manage',
  RECORD_MONEY: 'accounts:entry',

  /**
   * The general ledger.
   *
   * `MANAGE_CHART` is separate from `MANAGE_ACCOUNTS` because they are
   * different acts with different consequences. Adding a bank account is
   * routine; moving "Bank Accounts" under Indirect Expenses breaks every
   * balance sheet the business will ever print, and the people who should be
   * able to do the first are not automatically the people who should be able
   * to do the second.
   *
   * `LOCK_PERIOD` is narrower still. Locking a month is what stands between a
   * filed return and a backdated voucher, and reopening one changes a figure
   * somebody has already reported — so `canReopenPeriod` below is owner-only,
   * separately from this.
   */
  MANAGE_CHART: 'accounting:chart',
  LOCK_PERIOD: 'accounting:lock',

  /* Compliance and reporting */
  VIEW_REPORTS: 'reports:view',
  FILE_RETURNS: 'returns:file',

  /**
   * Taking data out.
   *
   * Separate from `VIEW_REPORTS` because a backup is not a bigger report. The
   * whole tenant leaves as one file that a restore can be built from, and that
   * is an owner's decision rather than a consequence of being able to read a
   * screen. See the note on `/data` in the route table.
   */
  EXPORT_DATA: 'data:export',

  /* Administration */
  MANAGE_BUSINESS: 'business:manage',
  MANAGE_TEAM: 'team:manage',
  MANAGE_SETTINGS: 'settings:manage',
};

const ALL = Object.values(CAPABILITIES);

const MATRIX = {
  [ROLES.OWNER]: new Set(ALL),

  [ROLES.ADMIN]: new Set(
    ALL.filter(
      (capability) =>
        /* A manager runs the shop and can bring people in, but cannot change
           who owns it or edit the registered business details. */
        capability !== CAPABILITIES.MANAGE_BUSINESS
    )
  ),

  [ROLES.ACCOUNTANT]: new Set([
    CAPABILITIES.CREATE_VOUCHER,
    CAPABILITIES.CANCEL_VOUCHER,
    /**
     * An accountant may EDIT a posted voucher.
     *
     * This is the role the capability exists for. Correcting a mis-posted
     * entry is the job — refusing it does not protect the books, it moves the
     * correction into a journal that says less than the fix would have, or
     * into a spreadsheet nobody can audit. What protects the books is the
     * revision trail and the period lock, both of which apply here.
     */
    CAPABILITIES.EDIT_VOUCHER,
    CAPABILITIES.RECORD_PAYMENT,
    CAPABILITIES.MANAGE_PARTIES,
    CAPABILITIES.RECORD_MONEY,
    CAPABILITIES.MANAGE_ACCOUNTS,
    CAPABILITIES.MANAGE_CHART,
    CAPABILITIES.VIEW_REPORTS,
    CAPABILITIES.FILE_RETURNS,
    /* Not stock: an accountant reconciles the books, and a stock adjustment is
       a physical claim about the shelf that only somebody at the shelf can
       make. */
  ]),

  /**
   * Counter staff: everything on the sell side, nothing on the buy side.
   *
   * They get `MANAGE_PARTIES` because a walk-in customer has to be added at
   * the till or the sale does not happen, and `RECORD_PAYMENT` because taking
   * the money is the job. They do NOT get `CANCEL_VOUCHER`: cancelling an
   * invoice is how a till is emptied without a trail, and it is a decision for
   * whoever counts the till rather than whoever runs it.
   */
  [ROLES.SALES_USER]: new Set([
    CAPABILITIES.CREATE_SALES_VOUCHER,
    CAPABILITIES.RECORD_PAYMENT,
    CAPABILITIES.MANAGE_PARTIES,
    CAPABILITIES.VIEW_REPORTS,
  ]),

  /**
   * A buyer: the inward mirror, plus the stock capabilities, because the
   * person receiving goods is the person standing at the shelf — which is the
   * same reasoning that keeps stock away from the accountant.
   */
  [ROLES.PURCHASE_USER]: new Set([
    CAPABILITIES.CREATE_PURCHASE_VOUCHER,
    CAPABILITIES.RECORD_PAYMENT,
    CAPABILITIES.MANAGE_PARTIES,
    CAPABILITIES.MANAGE_ITEMS,
    CAPABILITIES.ADJUST_STOCK,
    CAPABILITIES.TRANSFER_STOCK,
    CAPABILITIES.STOCK_TAKE,
    CAPABILITIES.VIEW_REPORTS,
  ]),

  [ROLES.VIEWER]: new Set([CAPABILITIES.VIEW_REPORTS]),
};

/**
 * Holding the general capability implies holding both directional ones.
 *
 * Written here rather than repeated in every set, so that adding a third
 * direction later cannot leave an owner without it. An owner, a manager and an
 * accountant may raise anything; the two counter roles hold exactly one half.
 */
[ROLES.OWNER, ROLES.ADMIN, ROLES.ACCOUNTANT].forEach((role) => {
  if (MATRIX[role].has(CAPABILITIES.CREATE_VOUCHER)) {
    MATRIX[role].add(CAPABILITIES.CREATE_SALES_VOUCHER);
    MATRIX[role].add(CAPABILITIES.CREATE_PURCHASE_VOUCHER);
  }
});

/**
 * @param {string} role
 * @param {string} capability
 */
export function can(role, capability) {
  const set = MATRIX[role];
  return set ? set.has(capability) : false;
}

/**
 * Which capability raising THIS voucher type needs.
 *
 * A screen asks about the document in front of it rather than about a role,
 * which is what lets `Sales Staff` exist without every caller learning that
 * two directional capabilities were added. Holding the general
 * `CREATE_VOUCHER` satisfies either, because the sets above grant both to
 * anyone who has it.
 */
export function voucherCapabilityFor(type) {
  const config = tryVoucherConfig(type);
  if (!config) return CAPABILITIES.CREATE_VOUCHER;
  return config.weAreSupplier
    ? CAPABILITIES.CREATE_SALES_VOUCHER
    : CAPABILITIES.CREATE_PURCHASE_VOUCHER;
}

/** Whether a role may raise a document of this type. */
export function canCreateVoucher(role, type) {
  return can(role, CAPABILITIES.CREATE_VOUCHER) || can(role, voucherCapabilityFor(type));
}

/** Every capability a role has, for a "what can this person do" summary. */
export function capabilitiesOf(role) {
  return [...(MATRIX[role] || new Set())];
}

/**
 * Whether `actor` may change `target`'s role to `nextRole`.
 *
 * Mirrors the server's rules exactly, and the reasons matter:
 *
 *   ONLY AN OWNER MAKES AN OWNER. Otherwise a manager promotes themselves and
 *   the ownership of the business has changed hands through a dropdown.
 *
 *   NOBODY CHANGES THEIR OWN ROLE. Self-demotion is how the last owner
 *   accidentally locks everyone out; self-promotion is the obvious hole.
 *
 *   THE LAST OWNER CANNOT BE REMOVED OR DEMOTED. A business with no owner has
 *   nobody who can invite one, and the only fix is a support ticket.
 */
export function canChangeRole({ actorRole, actorUid, targetRole, targetUid, nextRole, ownerCount = 1 }) {
  if (!can(actorRole, CAPABILITIES.MANAGE_TEAM)) {
    return { ok: false, reason: 'Only an owner or a manager can change roles.' };
  }
  if (actorUid && targetUid && actorUid === targetUid) {
    return { ok: false, reason: 'You cannot change your own role. Ask another owner.' };
  }
  if (nextRole === ROLES.OWNER && actorRole !== ROLES.OWNER) {
    return { ok: false, reason: 'Only an owner can make somebody else an owner.' };
  }
  if (targetRole === ROLES.OWNER && actorRole !== ROLES.OWNER) {
    return { ok: false, reason: 'Only an owner can change another owner.' };
  }
  if (targetRole === ROLES.OWNER && nextRole !== ROLES.OWNER && ownerCount <= 1) {
    return {
      ok: false,
      reason: 'This is the last owner. Make somebody else an owner first, or the business would have nobody who can manage it.',
    };
  }
  return { ok: true, reason: null };
}

/**
 * Which capability each screen needs.
 *
 * WHY A TABLE AND NOT A CHECK IN EACH SCREEN
 * Because the failure mode of per-screen checks is the one this application
 * had: eight of about thirty screens checked, the rest open, and every role
 * seeing every link in the sidebar. A table is auditable in one place — you
 * can read it and say what a viewer can reach — and a route added without an
 * entry is a route somebody has to think about rather than one that silently
 * defaults to open.
 *
 * A `null` capability means "anyone signed in to this business", and it is
 * spelled out rather than left as an absent key, so the two are distinguished
 * on sight.
 *
 * THIS IS STILL NOT SECURITY. The database refuses what the rules refuse,
 * whatever the browser thinks; this stops a person being shown a door that
 * will not open. See the note at the top of this file.
 */
export const ROUTE_CAPABILITIES = {
  '/dashboard': null,
  '/pos': CAPABILITIES.CREATE_VOUCHER,

  '/parties': null,
  '/parties/new': CAPABILITIES.MANAGE_PARTIES,
  '/parties/:partyId/edit': CAPABILITIES.MANAGE_PARTIES,

  '/items': null,
  '/items/new': CAPABILITIES.MANAGE_ITEMS,
  '/items/catalogue': CAPABILITIES.MANAGE_ITEMS,
  '/items/labels': CAPABILITIES.MANAGE_ITEMS,
  '/items/:itemId/edit': CAPABILITIES.MANAGE_ITEMS,

  '/locations': CAPABILITIES.TRANSFER_STOCK,
  '/stock-take': CAPABILITIES.STOCK_TAKE,
  '/manufacturing': CAPABILITIES.MANAGE_ITEMS,
  '/manufacturing/boms/:bomId': CAPABILITIES.MANAGE_ITEMS,

  '/vouchers/:voucherRoute': null,
  '/vouchers/:voucherRoute/new': CAPABILITIES.CREATE_VOUCHER,
  '/vouchers/:voucherRoute/:voucherId': null,

  '/payments': null,
  '/accounts': CAPABILITIES.MANAGE_ACCOUNTS,
  '/accounts/cheques': CAPABILITIES.RECORD_MONEY,
  '/accounts/:accountId': CAPABILITIES.MANAGE_ACCOUNTS,
  '/accounts/:accountId/reconcile': CAPABILITIES.MANAGE_ACCOUNTS,

  /**
   * The general ledger.
   *
   * Reading is `VIEW_REPORTS`, which a viewer holds — an auditor's whole job is
   * to read the books and change nothing. Entering is `RECORD_MONEY`; editing a
   * posted voucher is `EDIT_VOUCHER`; changing the chart itself is
   * `MANAGE_CHART`.
   */
  '/accounting': CAPABILITIES.VIEW_REPORTS,
  '/accounting/day-book': CAPABILITIES.VIEW_REPORTS,
  '/accounting/trial-balance': CAPABILITIES.VIEW_REPORTS,
  '/accounting/balance-sheet': CAPABILITIES.VIEW_REPORTS,
  '/accounting/profit-loss': CAPABILITIES.VIEW_REPORTS,
  '/accounting/cash-flow': CAPABILITIES.VIEW_REPORTS,
  '/accounting/outstanding': CAPABILITIES.VIEW_REPORTS,
  '/accounting/ledgers/:ledgerId': CAPABILITIES.VIEW_REPORTS,
  '/accounting/vouchers/:voucherRoute': CAPABILITIES.VIEW_REPORTS,
  '/accounting/vouchers/:voucherRoute/new': CAPABILITIES.RECORD_MONEY,
  '/accounting/vouchers/:voucherRoute/:voucherId': CAPABILITIES.VIEW_REPORTS,
  '/accounting/vouchers/:voucherRoute/:voucherId/edit': CAPABILITIES.EDIT_VOUCHER,

  '/pricing': null,
  '/pricing/:priceListId': null,
  '/recurring': null,
  '/reminders': CAPABILITIES.VIEW_REPORTS,
  '/messages': CAPABILITIES.VIEW_REPORTS,
  '/reports': CAPABILITIES.VIEW_REPORTS,
  '/activity': CAPABILITIES.VIEW_REPORTS,
  /**
   * A BACKUP IS NOT A BIGGER REPORT.
   *
   * This was `VIEW_REPORTS`, which a viewer holds, and `runExport` itself
   * checked nothing — so anybody who could open a report could walk out with
   * the entire tenant as one restorable file.
   *
   * Being honest about what this does and does not fix: the rules deliberately
   * let any member READ their business's data, because that is what a viewer
   * is for, so this is the courtesy half — it stops the button being offered.
   * Making it a boundary means moving the export to a Cloud Function that
   * checks the role server-side, which is the right next step and a bigger
   * one.
   */
  '/data': CAPABILITIES.EXPORT_DATA,
  '/data/import': CAPABILITIES.MANAGE_ITEMS,
  '/store': CAPABILITIES.MANAGE_SETTINGS,

  '/settings': null,
  '/settings/:section': null,
};

/**
 * Whether a role may open a path.
 *
 * Unknown paths are ALLOWED, deliberately. A 404 route, a legacy redirect and
 * a path added in a hurry should not become invisible to everyone but an
 * owner — the failure would be silent and would look like a broken build. The
 * table above is the place to be deliberate; this function is the place to be
 * forgiving.
 */
export function canOpenRoute(role, path) {
  const capability = capabilityForPath(path);
  if (capability === undefined || capability === null) return true;
  /* Either directional capability is satisfied by the general one, so a role
     holding `CREATE_VOUCHER` is not refused a route resolved to one half. */
  if (
    capability === CAPABILITIES.CREATE_SALES_VOUCHER ||
    capability === CAPABILITIES.CREATE_PURCHASE_VOUCHER
  ) {
    return can(role, CAPABILITIES.CREATE_VOUCHER) || can(role, capability);
  }
  return can(role, capability);
}

/**
 * Find the table entry for a path, whether it is written with parameters or
 * with real values.
 *
 * BOTH FORMS REACH THIS. The route guard passes the pattern React Router
 * registered (`/vouchers/:voucherRoute/new`), and the sidebar passes the
 * concrete link a user would click (`/vouchers/sales/new`). Matching only the
 * first would leave every sidebar link unfiltered — which is not a visible
 * failure, it is a filter that quietly does nothing.
 *
 * A literal segment beats a parameter, and a longer pattern beats a shorter
 * one, so `/items/new` is not matched by `/items/:itemId`.
 */
export function capabilityForPath(path) {
  const segments = String(path || '').split('/').filter(Boolean);

  /**
   * `/vouchers/<route>/new` needs the capability for THAT type, not one answer
   * for all ten.
   *
   * The table has a single entry covering every voucher route, which was fine
   * while `CREATE_VOUCHER` was one capability and is exactly what stopped a
   * sales-only role from existing. Resolved before the table is consulted,
   * because the table cannot express it. The parameterised form the route
   * guard passes still falls through to the table's general answer, which is
   * the right one for a role holding both halves.
   */
  if (segments.length === 3 && segments[0] === 'vouchers' && segments[2] === 'new') {
    const type = voucherTypeFromRoute(segments[1]);
    if (type) return voucherCapabilityFor(type);
  }

  if (path in ROUTE_CAPABILITIES) return ROUTE_CAPABILITIES[path];

  const candidates = Object.keys(ROUTE_CAPABILITIES)
    .map((pattern) => ({ pattern, parts: pattern.split('/').filter(Boolean) }))
    .filter((entry) => entry.parts.length === segments.length)
    .filter((entry) =>
      entry.parts.every(
        (part, index) => part.startsWith(':') || part === segments[index]
      )
    )
    /* Fewest parameters wins: `/items/new` must not be answered by
       `/items/:itemId`, which would give a viewer the edit screen. */
    .sort(
      (a, b) =>
        a.parts.filter((part) => part.startsWith(':')).length -
        b.parts.filter((part) => part.startsWith(':')).length
    );

  return candidates.length ? ROUTE_CAPABILITIES[candidates[0].pattern] : undefined;
}

/** The reason, for the screen that has to explain a locked door. */
export function routeRefusal(role, path) {
  if (canOpenRoute(role, path)) return null;
  return `Your role — ${roleLabel(role)} — cannot open this. Ask an owner or a manager.`;
}

/**
 * Whether a role may reopen a locked accounting period.
 *
 * Owner only, and separate from `LOCK_PERIOD` on purpose. Locking a month is
 * housekeeping; reopening one changes a figure that has already been reported
 * to somebody — a bank, a customer, the GST portal. A manager who can lock and
 * unlock at will has a lock that means nothing.
 */
export function canReopenPeriod(role) {
  return role === ROLES.OWNER;
}

export function canRemoveMember({ actorRole, actorUid, targetRole, targetUid, ownerCount = 1 }) {
  if (!can(actorRole, CAPABILITIES.MANAGE_TEAM)) {
    return { ok: false, reason: 'Only an owner or a manager can remove people.' };
  }
  if (actorUid && targetUid && actorUid === targetUid) {
    return { ok: false, reason: 'You cannot remove yourself.' };
  }
  if (targetRole === ROLES.OWNER && actorRole !== ROLES.OWNER) {
    return { ok: false, reason: 'Only an owner can remove another owner.' };
  }
  if (targetRole === ROLES.OWNER && ownerCount <= 1) {
    return { ok: false, reason: 'This is the last owner and cannot be removed.' };
  }
  return { ok: true, reason: null };
}
